/**
 * Utility functions for Assembly Form Dialog
 * Contains helper functions for string normalization, time calculations, and validation
 */

import { MAX_HHMM_HOURS_DIGITS } from '../../constants/hhmmHoursLimits';

// Helper function to normalize strings for matching (remove common suffixes, normalize spaces)
export const normalizeForMatching = (str) => {
  if (!str) return '';
  return str.trim()
    .toLowerCase()
    .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
    .replace(/[^\w\s]/g, '') // Remove special characters except word chars and spaces
    .trim();
};

// Helper function specifically for model names (handles variations like "G280" vs "G-280")
export const normalizeModelName = (str) => {
  if (!str) return '';
  return normalizeForMatching(str)
    .replace(/\s*-\s*/g, '') // Remove hyphens and spaces around them (G-280 -> G280)
    .replace(/\s+/g, ''); // Remove all spaces for model names
};

// Helper function to check if two strings match (exact or partial)
export const stringsMatch = (str1, str2) => {
  const normalized1 = normalizeForMatching(str1);
  const normalized2 = normalizeForMatching(str2);
  
  // Exact match
  if (normalized1 === normalized2) return true;
  
  // Check if one contains the other (for cases like "Gulfstream" matching "Gulfstream Aerospace")
  if (normalized1.includes(normalized2) || normalized2.includes(normalized1)) {
    // Only consider it a match if the shorter string is at least 3 characters
    const shorter = normalized1.length < normalized2.length ? normalized1 : normalized2;
    return shorter.length >= 3;
  }
  
  return false;
};

// Helper function to convert time string (HH:MM) to minutes
export const timeToMinutes = (timeStr) => {
  if (!timeStr || !timeStr.includes(':')) return 0;
  const [hours, minutes] = timeStr.split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};

// Helper function to convert minutes to time string (HH:MM)
export const minutesToTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

// Helper function to calculate elapsed time in years and months from dates
export const calculateYearsFromDates = (startDate, asOnDate) => {
  if (!startDate || !asOnDate) return '0.00';
  const start = new Date(startDate);
  const end = new Date(asOnDate);
  
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return '0.00';
  
  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();
  
  if (months < 0) {
    years--;
    months += 12;
  }
  
  if (end.getDate() < start.getDate()) {
    months--;
    if (months < 0) {
      years--;
      months += 12;
    }
  }
  
  if (months >= 12) {
    years += Math.floor(months / 12);
    months = months % 12;
  }
  
  if (years > 99) {
    years = 99;
  }
  
  const monthsStr = String(months).padStart(2, '0');
  return `${years}.${monthsStr}`;
};

// Helper function to calculate elapsed time in calendar days from dates
export const calculateCalendarDaysFromDates = (startDate, asOnDate) => {
  if (!startDate || !asOnDate) return '0';
  const start = new Date(startDate);
  const end = new Date(asOnDate);
  
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return '0';
  
  // Calculate difference in milliseconds
  const diffTime = end.getTime() - start.getTime();
  // Convert to days (round to nearest integer)
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
  
  // Ensure non-negative and max 5 digits (99999)
  const days = Math.max(0, Math.min(diffDays, 99999));
  
  return days.toString();
};

// Helper function to parse years.months format to decimal
export const parseYearsMonthsToDecimal = (valueStr) => {
  if (!valueStr) return 0;
  if (valueStr.includes('.')) {
    const [years, months] = valueStr.split('.');
    const yearsNum = parseInt(years) || 0;
    const monthsNum = parseInt(months) || 0;
    return yearsNum + (monthsNum / 12);
  }
  const parsed = parseFloat(valueStr);
  return isNaN(parsed) ? 0 : parsed;
};

// Helper function to format decimal years to YY.MM format
export const formatDecimalToYearsMonths = (decimalValue) => {
  if (isNaN(decimalValue) || decimalValue < 0) return '0.00';
  const years = Math.floor(decimalValue);
  const months = Math.round((decimalValue - years) * 12);
  const finalMonths = months >= 12 ? 11 : months;
  const monthsStr = String(finalMonths).padStart(2, '0');
  return `${years}.${monthsStr}`;
};

// Helper function to calculate End Date from As On Date + Remaining Time
export const calculateEndDate = (asOnDate, remainingTime) => {
  if (!asOnDate || !remainingTime?.value || !remainingTime?.unit) return null;
  
  const baseDate = new Date(asOnDate);
  if (isNaN(baseDate.getTime())) return null;
  
  const unit = remainingTime.unit;
  const value = remainingTime.value;
  
  if (unit === 'Hours') {
    // Parse HH:MM format
    if (!value || !value.includes(':')) return null;
    const [hours, minutes] = value.split(':').map(Number);
    const totalMinutes = (hours || 0) * 60 + (minutes || 0);
    const endDate = new Date(baseDate);
    endDate.setMinutes(endDate.getMinutes() + totalMinutes);
    return endDate;
  } else if (unit === 'Years') {
    // Parse YY.MM format
    if (!value || !value.includes('.')) return null;
    const [years, months] = value.split('.').map(Number);
    const endDate = new Date(baseDate);
    endDate.setFullYear(endDate.getFullYear() + (years || 0));
    endDate.setMonth(endDate.getMonth() + (months || 0));
    return endDate;
  } else if (unit === 'Calendar Days') {
    // Parse integer days
    const days = parseInt(value) || 0;
    const endDate = new Date(baseDate);
    endDate.setDate(endDate.getDate() + days);
    return endDate;
  }
  
  return null;
};

// Helper function to generate explanation text for End Date calculation
export const getEndDateCalculationExplanation = (asOnDate, remainingTime) => {
  if (!asOnDate || !remainingTime?.value || !remainingTime?.unit) {
    return 'End Date will be calculated when As On Date and Remaining Time are provided.';
  }
  
  const baseDate = new Date(asOnDate);
  if (isNaN(baseDate.getTime())) {
    return 'Invalid As On Date.';
  }
  
  const unit = remainingTime.unit;
  const value = remainingTime.value;
  
  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  const asOnDateFormatted = formatDate(baseDate);
  
  if (unit === 'Hours') {
    if (!value || !value.includes(':')) {
      return 'Invalid Hours format. Expected HH:MM.';
    }
    const [hours, minutes] = value.split(':').map(Number);
    const totalMinutes = (hours || 0) * 60 + (minutes || 0);
    const endDate = calculateEndDate(asOnDate, remainingTime);
    
    if (!endDate) return 'Unable to calculate End Date.';
    
    const endDateFormatted = formatDate(endDate);
    const hoursText = hours > 0 ? `${hours} hour${hours !== 1 ? 's' : ''}` : '';
    const minutesText = minutes > 0 ? `${minutes} minute${minutes !== 1 ? 's' : ''}` : '';
    const timeText = [hoursText, minutesText].filter(Boolean).join(' and ') || '0 minutes';
    
    return `As On Date (${asOnDateFormatted}) + Remaining Time (${timeText}) = ${endDateFormatted}`;
  } else if (unit === 'Years') {
    if (!value || !value.includes('.')) {
      return 'Invalid Years format. Expected YY.MM.';
    }
    const [years, months] = value.split('.').map(Number);
    const endDate = calculateEndDate(asOnDate, remainingTime);
    
    if (!endDate) return 'Unable to calculate End Date.';
    
    const endDateFormatted = formatDate(endDate);
    const yearsText = years > 0 ? `${years} year${years !== 1 ? 's' : ''}` : '';
    const monthsText = months > 0 ? `${months} month${months !== 1 ? 's' : ''}` : '';
    const timeText = [yearsText, monthsText].filter(Boolean).join(' and ') || '0 months';
    
    return `As On Date (${asOnDateFormatted}) + Remaining Time (${timeText}) = ${endDateFormatted}`;
  } else if (unit === 'Calendar Days') {
    const days = parseInt(value) || 0;
    const endDate = calculateEndDate(asOnDate, remainingTime);
    
    if (!endDate) return 'Unable to calculate End Date.';
    
    const endDateFormatted = formatDate(endDate);
    const daysText = `${days} day${days !== 1 ? 's' : ''}`;
    
    return `As On Date (${asOnDateFormatted}) + Remaining Time (${daysText}) = ${endDateFormatted}`;
  }
  
  return 'Unable to calculate End Date.';
};

// Helper function to validate and format Years input (YY.MM format)
export const validateYearsInput = (value) => {
  if (!value) return '';
  
  let cleaned = value.replace(/[^\d.]/g, '');
  
  const parts = cleaned.split('.');
  if (parts.length > 2) {
    cleaned = parts[0] + '.' + parts.slice(1).join('');
  }
  
  if (parts.length === 2) {
    const wholePart = parts[0];
    const decimalPart = parts[1];
    
    if (wholePart.length > 2) {
      cleaned = wholePart.substring(0, 2) + (decimalPart ? '.' + decimalPart : '');
    }
    
    if (decimalPart.length > 2) {
      cleaned = wholePart + '.' + decimalPart.substring(0, 2);
    }
    
    const months = parseInt(decimalPart) || 0;
    if (months > 12) {
      cleaned = wholePart + '.12';
    }
  } else if (parts.length === 1) {
    if (parts[0].length > 2) {
      cleaned = parts[0].substring(0, 2);
    }
  }
  
  return cleaned;
};

// Helper function to validate Calendar Days input (max 5 digits, no decimals)
export const validateCalendarDaysInput = (value) => {
  if (!value) return '';
  
  // Remove any non-digit characters
  let cleaned = value.replace(/[^\d]/g, '');
  
  // Limit to 5 digits maximum
  if (cleaned.length > 5) {
    cleaned = cleaned.substring(0, 5);
  }
  
  return cleaned;
};

// Helper function to validate Cycles input (numeric with up to 2 decimal places)
export const validateCyclesInput = (value) => {
  if (!value) return '';
  const trimmed = value.trim();
  // Allow digits and at most one decimal point with max 2 digits after
  const match = trimmed.match(/^\d*\.?\d{0,2}/);
  if (!match || !match[0]) return '';
  const cleaned = match[0];
  if (cleaned === '.') return '';
  return cleaned;
};

// Parse Cycles value (allows decimals, round to 2 decimal places for calculations)
export const parseCyclesValue = (valueStr) => {
  if (!valueStr) return 0;
  const parsed = parseFloat(String(valueStr).replace(/,/g, ''));
  return isNaN(parsed) ? 0 : Math.round(parsed * 100) / 100;
};

// Helper function to validate Hours input (HH:MM format)
// Returns: { value: string, error: string }
export const validateHoursInput = (value) => {
  if (!value || value.trim() === '') {
    return { value: '', error: '' };
  }
  
  const trimmed = value.trim();
  
  // Check if colon is present
  const colonIndex = trimmed.indexOf(':');
  
  if (colonIndex === -1) {
    const digitsOnly = trimmed.replace(/[^\d]/g, '');
    if (digitsOnly.length > MAX_HHMM_HOURS_DIGITS) {
      return {
        value: digitsOnly.substring(0, MAX_HHMM_HOURS_DIGITS),
        error: `Hours cannot exceed ${MAX_HHMM_HOURS_DIGITS} digits`
      };
    }
    if (trimmed !== digitsOnly && digitsOnly.length > 0) {
      return {
        value: digitsOnly,
        error: 'Only digits allowed before colon'
      };
    }
    return { value: digitsOnly, error: '' };
  }
  
  // Colon is present - validate both parts
  const hoursPart = trimmed.substring(0, colonIndex);
  const minutesPart = trimmed.substring(colonIndex + 1);
  
  let validHours = hoursPart.replace(/[^\d]/g, '');
  if (validHours.length > MAX_HHMM_HOURS_DIGITS) {
    validHours = validHours.substring(0, MAX_HHMM_HOURS_DIGITS);
  }
  
  // Validate minutes part (0-59, max 2 digits)
  let validMinutes = minutesPart.replace(/[^\d]/g, '');
  if (validMinutes.length > 2) {
    validMinutes = validMinutes.substring(0, 2);
  }
  
  // Check if minutes exceed 59
  const minutesNum = parseInt(validMinutes) || 0;
  if (minutesNum > 59) {
    validMinutes = '59';
  }
  
  // Build the validated value
  const validatedValue = validHours + ':' + validMinutes;
  
  // Determine error message
  let error = '';
  if (hoursPart !== validHours && hoursPart.length > 0) {
    error = `Hours must be digits only (max ${MAX_HHMM_HOURS_DIGITS} digits)`;
  } else if (minutesPart !== validMinutes && minutesPart.length > 0) {
    error = 'Minutes must be 0-59 (2 digits max)';
  } else if (minutesNum > 59 && minutesPart.length > 0) {
    error = 'Minutes cannot exceed 59';
  }
  
  return { value: validatedValue, error };
};

// Helper function to format Hours value - ensures colon is present, adds ":00" if missing
export const formatHoursValue = (value) => {
  if (!value || value.trim() === '') return value;
  
  const trimmed = value.trim();
  
  // If value already contains a colon, validate and return
  if (trimmed.includes(':')) {
    const validation = validateHoursInput(trimmed);
    return validation.value;
  }
  
  // If value is just digits, append ":00"
  if (/^\d+$/.test(trimmed)) {
    const hours =
      trimmed.length > MAX_HHMM_HOURS_DIGITS
        ? trimmed.substring(0, MAX_HHMM_HOURS_DIGITS)
        : trimmed;
    return `${hours}:00`;
  }
  
  // Otherwise validate and return
  const validation = validateHoursInput(trimmed);
  return validation.value;
};

// Helper function to parse numeric value (for Calendar Days - integer only)
export const parseNumericValue = (valueStr) => {
  if (!valueStr) return 0;
  // For Calendar Days, parse as integer (no decimals)
  const parsed = parseInt(valueStr, 10);
  return isNaN(parsed) ? 0 : parsed;
};

// Clean and format timeSinceRecords before submission
export const prepareTimeSinceRecordsForSubmission = (records) => {
  if (!records || records.length === 0) return [];
  
  return records.map(record => {
    const elapsedUnit = record.elapsedTime?.unit || 'Hours';
    const elapsedValue = record.elapsedTime?.value || (
      elapsedUnit === 'Hours' ? '00:00' : 
      elapsedUnit === 'Years' ? '0.00' : 
      elapsedUnit === 'Calendar Days' ? '0' : '0'
    );
    const remainingUnit = record.remainingTime?.unit || elapsedUnit;
    const remainingValue = record.remainingTime?.value || (
      remainingUnit === 'Hours' ? '00:00' : 
      remainingUnit === 'Years' ? '0.00' : 
      remainingUnit === 'Calendar Days' ? '0' : '0'
    );
    
    let startDate = new Date();
    let asOnDate = new Date();
    try {
      if (record.startDate) {
        const parsedStart = new Date(record.startDate);
        if (!isNaN(parsedStart.getTime())) startDate = parsedStart;
      }
      if (record.asOnDate) {
        const parsedAsOn = new Date(record.asOnDate);
        if (!isNaN(parsedAsOn.getTime())) asOnDate = parsedAsOn;
      }
    } catch (e) {
      console.error('Error parsing dates:', e);
    }

    // Clean numeric units: Calendar Days/Landings = integer; Cycles = up to 2 decimals
    let finalElapsedValue = String(elapsedValue).trim();
    let finalRemainingValue = String(remainingValue).trim();
    
    if (elapsedUnit === 'Calendar Days' || elapsedUnit === 'Landings') {
      finalElapsedValue = Math.floor(parseFloat(finalElapsedValue) || 0).toString();
    } else if (elapsedUnit === 'Cycles') {
      finalElapsedValue = (Math.round(parseFloat(finalElapsedValue) * 100) / 100).toString();
    }
    if (remainingUnit === 'Calendar Days' || remainingUnit === 'Landings') {
      finalRemainingValue = Math.floor(parseFloat(finalRemainingValue) || 0).toString();
    } else if (remainingUnit === 'Cycles') {
      finalRemainingValue = (Math.round(parseFloat(finalRemainingValue) * 100) / 100).toString();
    }

    const cleaned = {
      type: record.type || 'TSN',
      label: (record.label || '').trim(),
      elapsedTime: {
        value: finalElapsedValue,
        unit: String(elapsedUnit).trim()
      },
      remainingTime: {
        value: finalRemainingValue,
        unit: String(remainingUnit).trim()
      },
      startDate: startDate,
      asOnDate: asOnDate
    };

    let limitValue = record.limit?.value ? String(record.limit.value).trim() : '';
    const limitUnit = record.limit?.unit ? String(record.limit.unit).trim() : '';
    
    if (limitUnit === 'Calendar Days' || limitUnit === 'Landings') {
      limitValue = limitValue ? Math.floor(parseFloat(limitValue) || 0).toString() : limitValue;
    } else if (limitUnit === 'Cycles') {
      limitValue = limitValue ? (Math.round(parseFloat(limitValue) * 100) / 100).toString() : limitValue;
    }
    
    if (limitValue && limitValue !== '' && limitValue !== '0.00' && limitValue !== '00:00' && limitUnit) {
      cleaned.limit = {
        value: limitValue,
        unit: limitUnit
      };
    }

    const defaultUnit = limitUnit || elapsedUnit;

    const normalizeValueAtInstallation = (val) => {
      if (!val || (val.value == null || String(val.value).trim() === '')) return null;
      let v = String(val.value).trim();
      const u = (val.unit && String(val.unit).trim()) || defaultUnit;
      if (u === 'Calendar Days' || u === 'Landings') {
        v = Math.floor(parseFloat(v) || 0).toString();
      } else if (u === 'Cycles') {
        v = (Math.round(parseFloat(v) * 100) / 100).toString();
      }
      return { value: v, unit: u };
    };

    const airframeVal = normalizeValueAtInstallation(record.airframeValueAtInstallation) || (record.installedValue?.value != null && String(record.installedValue.value).trim() !== '' ? normalizeValueAtInstallation(record.installedValue) : null);
    if (airframeVal) cleaned.airframeValueAtInstallation = airframeVal;

    const parentVal = normalizeValueAtInstallation(record.parentAssemblyValueAtInstallation);
    if (parentVal) cleaned.parentAssemblyValueAtInstallation = parentVal;

    const selfVal = normalizeValueAtInstallation(record.assemblySelfValueAtInstallation);
    if (selfVal) cleaned.assemblySelfValueAtInstallation = selfVal;

    return cleaned;
  });
};
